import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LinkIcon from '@material-ui/icons/Link';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minHeight: '400px',
    maxHeight: '80vh'
  },
  title: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  content: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(2)
  },
  termsBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: '300px',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.default,
    textAlign: 'justify'
  },
  checkboxContainer: {
    marginTop: theme.spacing(2)
  }
}));

const TermsAndConditionsPopup = ({ open, onAccept, onCancel }) => {
  const classes = useStyles();
  const [accepted, setAccepted] = useState(false);

  useEffect(
    () => {
      if (open) {
        setAccepted(false);
      }
    },
    [open]
  );

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.title}>
        AVISO IMPORTANTE – EXPORTA FÁCIL PARAGUAY
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Typography variant="body1" gutterBottom>
          Bienvenido a ExportaFácil. Antes de continuar, por favor lea y acepte
          los términos y condiciones.
        </Typography>

        <Box className={classes.termsBox}>
          <Typography variant="body2" paragraph>
            El uso del servicio EXPORTA FÁCIL implica la aceptación expresa de
            este aviso. Se exhorta a exportadores e importadores a informarse
            adecuadamente sobre los requisitos comerciales, técnicos y aduaneros
            tanto en el país de origen como en el país de destino. El servicio
            EXPORTA FÁCIL ofrecido por el Ministerio de Industria y Comercio
            (MIC) y operado a través de la Dirección Nacional de Correos del
            Paraguay (DINACOPA) constituye un régimen simplificado de
            exportación por vía postal, y no debe entenderse como una
            compraventa regular ni como una operación exenta de los trámites y
            responsabilidades propias de una exportación.
          </Typography>

          <Typography variant="h6" gutterBottom>
            1. Responsabilidad del exportador e importador
          </Typography>
          <Typography variant="body2" component="div" paragraph>
            <ul style={{ marginTop: 0, paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}>
                El exportador es plenamente responsable de realizar, a su costo
                y riesgo, todas las gestiones requeridas en el país de origen,
                cumpliendo con los requisitos aduaneros, fitosanitarios, de
                seguridad, documentación y etiquetado correspondientes.
              </li>
              <li style={{ marginBottom: '8px' }}>
                El importador es responsable de gestionar la desaduanización y
                nacionalización del envío en destino, incluyendo el pago de
                impuestos, aranceles, permisos y otros requisitos exigidos por
                las autoridades del país receptor.
              </li>
              <li>
                Ni el MIC, ni DINACOPA, ni ningún organismo asociado al servicio
                EXPORTA FÁCIL asumirán responsabilidad alguna por demoras,
                devoluciones, pérdidas, retenciones, costos adicionales o
                sanciones derivadas del incumplimiento de las normas por parte
                del exportador o importador.
              </li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom>
            2. Alcance del servicio
          </Typography>
          <Typography variant="body2" component="div" paragraph>
            EXPORTA FÁCIL facilita el envío de paquetes internacionales mediante
            despacho postal simplificado, pero no reemplaza ni elimina las
            obligaciones legales y aduaneras aplicables a las exportaciones e
            importaciones. Se recomienda a los usuarios informarse previamente
            sobre:
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li style={{ marginBottom: '8px' }}>
                Las normas aduaneras, aranceles y certificaciones del país de
                destino.
              </li>
              <li style={{ marginBottom: '8px' }}>
                Las regulaciones de exportación del Paraguay y los requisitos de
                origen.
              </li>
              <li>
                Los costos asociados a devoluciones, retenciones o destrucción
                de mercaderías no conformes.
              </li>
            </ul>
          </Typography>

          <Typography variant="h6" gutterBottom>
            3. Costos y devoluciones
          </Typography>
          <Typography variant="body2" paragraph>
            Cualquier costo derivado de la devolución, retención o reexpedición
            de envíos por incumplimiento será única y exclusivamente
            responsabilidad del exportador o importador. El MIC, DINACOPA o el
            servicio EXPORTA FÁCIL no asumirán costos, reembolsos ni gestiones
            ante estas situaciones.
          </Typography>

          <Typography variant="h6" gutterBottom>
            4. Obligación de informarse
          </Typography>
          <Typography variant="body2" component="div" paragraph>
            El uso del servicio EXPORTA FÁCIL implica la aceptación expresa de
            este aviso. Se exhorta a exportadores e importadores a informarse
            adecuadamente sobre los requisitos comerciales, técnicos y aduaneros
            tanto en el país de origen como en el país de destino.
            <Box mt={2}>
              <Typography variant="body2" gutterBottom>
                Para más información sobre el servicio EXPORTA FÁCIL, visite:
              </Typography>
              <Box display="flex" alignItems="center" mb={1}>
                <LinkIcon fontSize="small" style={{ marginRight: 8 }} />
                <Link
                  href="https://www.mipymes.gov.py/internacionalizacion/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.mipymes.gov.py/internacionalizacion/
                </Link>
              </Box>

              <Typography variant="body2" gutterBottom>
                Para calcular los costos de envío con DINACOPA, acceda a la
                calculadora de tarifas en:
              </Typography>
              <Box display="flex" alignItems="center" mb={1}>
                <LinkIcon fontSize="small" style={{ marginRight: 8 }} />
                <Link
                  href="https://correoparaguayo.gov.py/sitio/servicios/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://correoparaguayo.gov.py/sitio/servicios/
                </Link>
              </Box>

              <Typography variant="body2" gutterBottom>
                Para recibir asistencia técnica, capacitación y acompañamiento
                en procesos de internacionalización, visite la plataforma del
                Viceministerio de MIPYMES – Programa de Internacionalización de
                MIPYMES:
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <LinkIcon fontSize="small" style={{ marginRight: 8 }} />
                <Link
                  href="https://www.mipymes.gov.py/internacionalizacion"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.mipymes.gov.py/internacionalizacion
                </Link>
              </Box>

              <Typography variant="body2">
                Para más información, llame o escriba por WhatsApp al contact
                center de MIPYMES: <strong>021 729 7373</strong>
                <br />O escríbanos al correo:{' '}
                <Link href="mailto:dgii@mic.gov.py">dgii@mic.gov.py</Link>
              </Typography>
            </Box>
          </Typography>
        </Box>

        <FormControlLabel
          className={classes.checkboxContainer}
          control={
            <Checkbox
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              color="primary"
            />
          }
          label="He leído y acepto los términos y condiciones"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleAccept}
          color="primary"
          variant="contained"
          disabled={!accepted}
        >
          Aceptar y Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndConditionsPopup;
